import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, Check, X, AlertCircle, Filter, Building, Calendar, Eye, EyeOff, MessageSquare, Plus } from 'lucide-react';

// Category definitions for the system
export const CATEGORY_DEFINITIONS = [
  {
    name: "General",
    subCategories: [
      "Miscellaneous", "Random Knowledge", "General Interest", "Uncategorized"
    ],
    keywords: [
      "general", "miscellaneous", "random", "various", "other", "mixed",
      "different", "assorted", "general knowledge", "trivia", "facts",
      "common", "basic", "simple", "everyday", "regular"
    ]
  },
  {
    name: "Casino & Betting",
    subCategories: [
      "Sports Betting", "Casino Games", "Gambling Odds", "Gaming Strategy", "Betting Platforms"
    ],
    keywords: [
      "casino", "betting", "gambling", "poker", "blackjack", "roulette", "slots",
      "sports bet", "odds", "wager", "jackpot", "lottery", "bingo", "dice",
      "cards", "wheel", "spin", "bet", "stake", "payout", "winnings", "betfred",
      "gaming", "chance", "luck", "risk", "bookmaker", "sportsbook"
    ]
  },
  {
    name: "Learning & Education",
    subCategories: [
      "Online Learning", "Skill Development", "Study Motivation", "Digital Literacy", "Career Growth"
    ],
    keywords: [
      "learn", "course", "study", "workshop", "free learning", "online class",
      "training", "skills", "education", "e-learning", "digital literacy",
      "study tools", "tutoring", "bursary", "academic help", "online study",
      "student", "certificate"
    ]
  },
  {
    name: "Competitions & Rewards",
    subCategories: [
      "Prize Motivation", "Favorite Rewards", "Entry Behavior", "Brand Competitions"
    ],
    keywords: [
      "competition", "prize", "voucher", "win", "reward", "giveaway", "promo",
      "challenge", "draw", "entry", "loyalty", "points", "free", "Nando's",
      "KFC", "Shoprite", "Checkers", "Takealot", "Airtime"
    ]
  },
  {
    name: "Gaming & Entertainment",
    subCategories: [
      "Mobile Gaming", "Online Fun", "Esports & Streaming", "Platforms & Habits"
    ],
    keywords: [
      "game", "gaming", "play", "online", "esports", "fun", "entertainment",
      "app", "mobile", "PC", "console", "streaming", "Twitch", "YouTube",
      "Facebook gaming", "Discord", "multiplayer", "luck", "skill", "challenge",
      "daily play", "in-game", "digital content"
    ]
  },
  {
    name: "Shopping & E-Commerce",
    subCategories: [
      "Online Shopping Habits", "Deals & Savings", "Trusted Platforms", "Digital Purchases"
    ],
    keywords: [
      "shop", "shopping", "online deals", "e-commerce", "voucher", "rewards",
      "Checkers", "Shoprite", "Takealot", "price", "discount", "sale",
      "promotion", "product", "basket", "wishlist", "cart", "spending",
      "purchase", "smart shopping", "save money", "budget"
    ]
  },
  {
    name: "Financial Habits & Savings",
    subCategories: [
      "Budgeting", "Saving Goals", "Spending Behavior", "Rewards Programs"
    ],
    keywords: [
      "money", "budget", "save", "saving", "expense", "finance",
      "financial confidence", "payday", "spending", "rewards program", "goals",
      "13th cheque", "bonus", "festive spending", "side hustle", "income",
      "extra cash", "financial goals", "confidence", "money habits"
    ]
  },
  {
    name: "Lifestyle & Personality",
    subCategories: [
      "Daily Habits", "Preferences", "Travel & Mobility", "Aspirations"
    ],
    keywords: [
      "lifestyle", "relaxation", "unwind", "leisure", "fun", "transport",
      "travel", "mobility", "dream job", "aspirations", "motivation",
      "behavior", "attitude", "confidence", "personal goals", "hobbies",
      "relaxation", "preferences", "mountain", "beach", "city", "backpack",
      "flight", "window seat", "aisle seat"
    ]
  },
  {
    name: "Technology & Devices",
    subCategories: [
      "Mobile Usage", "Device Upgrades", "Digital Access", "Connectivity"
    ],
    keywords: [
      "mobile", "smartphone", "gadget", "device", "data", "airtime",
      "connection", "Wi-Fi", "streaming", "online access", "upgrade",
      "app usage", "mobile data", "subscriptions", "internet", "technology",
      "digital tools"
    ]
  },
  {
    name: "Child & Family Learning",
    subCategories: [
      "Tutoring Support", "Study Help", "Family Education", "Children's Skills"
    ],
    keywords: [
      "children", "kids", "tutoring", "extra lessons", "education",
      "study help", "learning support", "online tools", "family", "parenting",
      "schooling", "academic progress", "education apps"
    ]
  },
  {
    name: "Brand Engagement",
    subCategories: [
      "Brand Preference", "Competition Motivation", "Customer Loyalty", "Prize Appeal"
    ],
    keywords: [
      "brand", "Nando's", "KFC", "Shoprite", "Checkers", "Takealot",
      "loyalty", "reward", "trust", "engagement", "campaign", "promotion",
      "voucher preference", "customer choice", "consumer habits"
    ]
  },
  {
    name: "Goals & Aspirations",
    subCategories: [
      "2026 Goals", "Motivation", "Career Ambitions", "Confidence & Progress"
    ],
    keywords: [
      "goal", "aspiration", "motivation", "confidence", "achievement",
      "progress", "2026 challenge", "self-improvement", "development",
      "ambition", "future", "growth", "personal success"
    ]
  }
];

const normalizeLabel = (value?: string) =>
  (value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\band\b/g, "and")
    .replace(/[^a-z0-9]/g, "");

const findCategoryByName = (name?: string) => {
  if (!name) return undefined;
  const normalized = normalizeLabel(name);
  if (!normalized) return undefined;

  return CATEGORY_DEFINITIONS.find((category) => {
    const normalizedCategory = normalizeLabel(category.name);
    return (
      normalizedCategory === normalized ||
      normalizedCategory.includes(normalized) ||
      normalized.includes(normalizedCategory)
    );
  });
};

const findSubCategoryByName = (categoryName: string, subCategoryName?: string) => {
  if (!subCategoryName) return undefined;
  const category = findCategoryByName(categoryName);
  if (!category) return undefined;

  const normalized = normalizeLabel(subCategoryName);
  if (!normalized) return undefined;

  return category.subCategories.find((subCategory) => {
    const normalizedSubCategory = normalizeLabel(subCategory);
    return (
      normalizedSubCategory === normalized ||
      normalizedSubCategory.includes(normalized) ||
      normalized.includes(normalizedSubCategory)
    );
  });
};

const normalizeKeywords = (keywords?: string[]) =>
  Array.from(
    new Set(
      (keywords || [])
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0),
    ),
  );

const analyzeCategoryFromText = (text: string) => {
  const lower = text.toLowerCase();
  let bestCategory:
    | {
        category: (typeof CATEGORY_DEFINITIONS)[number];
        matchedKeywords: string[];
        score: number;
      }
    | null = null;

  CATEGORY_DEFINITIONS.forEach((category) => {
    const matchedKeywords =
      category.keywords?.filter((keyword) =>
        lower.includes(keyword.toLowerCase()),
      ) || [];

    const score =
      matchedKeywords.length /
      Math.max(category.keywords?.length || 1, 1);

    if (!bestCategory || score > bestCategory.score) {
      bestCategory = {
        category,
        matchedKeywords,
        score,
      };
    }
  });

  if (!bestCategory || bestCategory.score === 0) {
    return null;
  }

  return bestCategory;
};

const inferSubCategoryForCategory = (
  category: (typeof CATEGORY_DEFINITIONS)[number],
  text: string,
) => {
  if (!category?.subCategories || category.subCategories.length === 0) {
    return "";
  }

  const lower = text.toLowerCase();
  let bestMatch = "";
  let bestScore = 0;

  category.subCategories.forEach((subCategory) => {
    const words = subCategory.split(/\s+/);
    const score = words.reduce((acc, word) => {
      if (!word) return acc;
      return lower.includes(word.toLowerCase()) ? acc + 1 : acc;
    }, 0);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = subCategory;
    }
  });

  if (bestScore > 0) {
    return bestMatch;
  }

  return category.subCategories[0] || "";
};
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"



interface Question {
  id: number;
  questionText: string;
  category?: string;
  competitionId: number;
  questionType: string;
  options: any;
  // Competition details
  competitionTitle?: string;
  competitionType?: string;
  competitionCategory?: string;
  competitionIsActive?: boolean;
  competitionStartDate?: string;
  competitionEndDate?: string;
}

interface CompetitionGroup {
  competitionId: number | null;
  competitionTitle: string;
  competitionType?: string;
  competitionCategory?: string;
  competitionIsActive?: boolean;
  competitionStartDate?: string;
  competitionEndDate?: string;
  questions: Question[];
}

interface QuestionsResponse {
  questions: Question[];
  groupedByCompetition: Record<string, CompetitionGroup>;
  summary: {
    totalQuestions: number;
    totalCompetitions: number;
    activeCompetitions: number;
  };
}

interface CategorizationResult {
  questionId: number;
  questionText: string;
  currentCategory?: string;
  currentSubCategory?: string;
  currentKeywords?: string[];
  currentBrand?: string;
  suggestedCategory?: string;
  suggestedSubCategory?: string;
  suggestedKeywords?: string[];
  suggestedBrand?: string;
  confidence?: number;
  reasoning?: string;
  error?: string;
}

interface Answer {
  id: number;
  questionId: number;
  competitionId: number;
  userId: string;
  answerText: string;
  submittedAt: string;
  // Joined competition details
  competitionTitle?: string;
  questionText?: string;
  username?: string;
  userEmail?: string;
}

interface AnswersResponse {
  answers: Answer[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  summary?: {
    totalAnswers: number;
    uniqueQuestions: number;
    uniqueCompetitions: number;
  };
}


// Helper function to extract brand name from competition title
const extractBrand = (title?: string): string => {
  if (!title) return 'Unknown';
  // Simple extraction: assumes brand is the first word or a known pattern
  // This can be expanded with more sophisticated logic or a mapping
  const parts = title.split(' ');
  if (parts.length > 0) {
    return parts[0]; // Assuming the first word is the brand
  }
  return 'Unknown';
};


function QuestionCategorizationContent() {
  const { toast } = useToast();
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [categorizationResults, setCategorizationResults] = useState<CategorizationResult[]>([]);
  const [selectedResults, setSelectedResults] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>('grouped');
  const [filterCompetition, setFilterCompetition] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [resultOverrides, setResultOverrides] = useState<Record<number, {
    category: string;
    subCategory?: string;
    keywords: string[];
  }>>({});
  const [keywordInputs, setKeywordInputs] = useState<Record<number, string>>({});

  // Fetch all competition questions
  const { data: questionsResponse, isLoading: loadingQuestions, error: questionsError } = useQuery<QuestionsResponse>({
    queryKey: ['/api/admin/all-questions'],
    queryFn: async () => {
      console.log('[QUESTION CATEGORIZATION] Fetching questions...');

      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          console.error('[QUESTION CATEGORIZATION] No valid session found');
          throw new Error('Authentication required');
        }

        console.log('[QUESTION CATEGORIZATION] Using session token');

        const response = await fetch('/api/admin/all-questions', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          }
        });

        console.log('[QUESTION CATEGORIZATION] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[QUESTION CATEGORIZATION] Response error:', errorText);
          throw new Error(`Failed to fetch questions: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        console.log('[QUESTION CATEGORIZATION] Questions received:', data.summary);

        return data;
      } catch (error) {
        console.error('[QUESTION CATEGORIZATION] Fetch error:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000
  });

  const questions = questionsResponse?.questions || [];
  const groupedQuestions = questionsResponse?.groupedByCompetition || {};
  const summary = questionsResponse?.summary;

  useEffect(() => {
    if (!categorizationResults || categorizationResults.length === 0) {
      setResultOverrides({});
      setKeywordInputs({});
      return;
    }

    const initialOverrides: Record<
      number,
      {
        category: string;
        subCategory?: string;
        keywords: string[];
      }
    > = {};

    categorizationResults.forEach((result) => {
      const matchedCategory =
        findCategoryByName(result.suggestedCategory) ||
        findCategoryByName(result.currentCategory);

      const fallbackCategoryAnalysis = analyzeCategoryFromText(
        result.questionText,
      );

      const resolvedCategory =
        matchedCategory || fallbackCategoryAnalysis?.category;
      const categoryName = resolvedCategory?.name || "";

      const matchedSubCategory =
        categoryName &&
        (findSubCategoryByName(
          categoryName,
          result.suggestedSubCategory || result.currentSubCategory,
        ) ||
          undefined);

      const inferredSubCategory =
        resolvedCategory &&
        inferSubCategoryForCategory(resolvedCategory, result.questionText);

      const combinedKeywords = normalizeKeywords([
        ...(result.suggestedKeywords || []),
        ...(result.currentKeywords || []),
        ...(fallbackCategoryAnalysis?.matchedKeywords || []),
      ]);

      const fallbackKeywords =
        combinedKeywords.length > 0
          ? combinedKeywords
          : resolvedCategory?.keywords?.slice(0, 5) || [];

      initialOverrides[result.questionId] = {
        category: categoryName,
        subCategory: matchedSubCategory || inferredSubCategory || "",
        keywords: normalizeKeywords(fallbackKeywords),
      };
    });

    setResultOverrides(initialOverrides);
    setKeywordInputs({});
  }, [categorizationResults]);

  // Filter questions based on selected filters
  const getFilteredQuestions = () => {
    let filtered = questions;

    if (filterCompetition !== 'all') {
      filtered = filtered.filter(q =>
        filterCompetition === 'no-competition'
          ? !q.competitionId
          : q.competitionId?.toString() === filterCompetition
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(q =>
        filterStatus === 'active' ? q.competitionIsActive : !q.competitionIsActive
      );
    }

    return filtered;
  };

  // Get questions that need categorization (missing category or subcategory)
  const getUncategorizedQuestions = () => {
    const filtered = getFilteredQuestions();
    return filtered.filter(q => 
      !q.category || 
      q.category === 'None' || 
      q.category === '' || 
      q.category === null ||
      q.category === undefined
    );
  };

  // Get unique competitions for filter dropdown
  const getCompetitionOptions = () => {
    const competitions = Object.values(groupedQuestions).map(group => ({
      id: group.competitionId?.toString() || 'no-competition',
      title: group.competitionTitle,
      isActive: group.competitionIsActive,
      questionCount: group.questions.length
    }));

    return competitions.sort((a, b) => {
      // Sort active competitions first, then by title
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return a.title.localeCompare(b.title);
    });
  };

  // Categorize questions mutation
  const categorizeMutation = useMutation({
    mutationFn: async (questionIds: number[]) => {
      console.log('[QUESTION CATEGORIZATION] Starting categorization for questions:', questionIds);

      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          console.error('[QUESTION CATEGORIZATION] No valid session found for categorization');
          throw new Error('Authentication required');
        }

        console.log('[QUESTION CATEGORIZATION] Using session token for categorization');

        const response = await fetch('/api/admin/questions/categorize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ questionIds })
        });

        console.log('[QUESTION CATEGORIZATION] Categorization response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json();
          console.error('[QUESTION CATEGORIZATION] Categorization error:', errorData);
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        const data = await response.json();
        console.log('[QUESTION CATEGORIZATION] Categorization results:', {
          total: data.results?.length || 0,
          successful: data.results?.filter((r: any) => !r.error).length || 0,
          errors: data.results?.filter((r: any) => r.error).length || 0
        });

        return data;
      } catch (error) {
        console.error('[QUESTION CATEGORIZATION] Categorization mutation error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('[QUESTION CATEGORIZATION] Categorization successful, processing results');
      setCategorizationResults(data.results || []);

      // Select all successfully categorized questions by default
      const successfulIds = (data.results || [])
        .filter((r: CategorizationResult) => r.suggestedCategory && !r.error)
        .map((r: CategorizationResult) => r.questionId);

      console.log('[QUESTION CATEGORIZATION] Auto-selecting successful categorizations:', successfulIds);
      setSelectedResults(successfulIds);

      const totalResults = data.results?.length || 0;
      const successfulResults = successfulIds.length;
      const errorResults = totalResults - successfulResults;

      toast({
        title: "Categorization Complete",
        description: `Analyzed ${totalResults} questions (${successfulResults} successful, ${errorResults} errors)`,
      });
    },
    onError: (error: Error) => {
      console.error('[QUESTION CATEGORIZATION] Categorization failed:', error);
      toast({
        title: "Categorization Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Apply categories mutation
  const applyMutation = useMutation({
    mutationFn: async (updates: Array<{
      questionId: number, 
      category: string, 
      subCategory?: string, 
      keywords?: string[], 
      brand?: string
    }>) => {
      console.log('[QUESTION CATEGORIZATION] Applying enhanced categories for updates:', updates);

      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          console.error('[QUESTION CATEGORIZATION] No valid session found for apply categories');
          throw new Error('Authentication required');
        }

        console.log('[QUESTION CATEGORIZATION] Using session token for apply categories');

        const response = await fetch('/api/admin/questions/apply-categories', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ updates })
        });

        console.log('[QUESTION CATEGORIZATION] Apply categories response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json();
          console.error('[QUESTION CATEGORIZATION] Apply categories error:', errorData);
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        const data = await response.json();
        console.log('[QUESTION CATEGORIZATION] Apply categories results:', data);

        return data;
      } catch (error) {
        console.error('[QUESTION CATEGORIZATION] Apply categories mutation error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('[QUESTION CATEGORIZATION] Categories applied successfully:', data);

      let message = `Updated ${data.success} questions successfully`;
      if (data.failed > 0) {
        message += `, ${data.failed} failed`;
      }

      toast({
        title: "Categories Applied",
        description: message,
        variant: data.failed > 0 ? "default" : "default"
      });

      setCategorizationResults([]);
      setSelectedResults([]);
      queryClient.invalidateQueries({ queryKey: ['/api/admin/all-questions'] });
    },
    onError: (error: Error) => {
      console.error('[QUESTION CATEGORIZATION] Apply categories failed:', error);
      toast({
        title: "Failed to Apply Categories",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleCategorizeAll = () => {
    const uncategorizedQuestions = getUncategorizedQuestions();
    if (!uncategorizedQuestions || uncategorizedQuestions.length === 0) {
      toast({
        title: "No Questions to Categorize",
        description: "All filtered questions already have categories assigned",
        variant: "default"
      });
      return;
    }
    const questionIds = uncategorizedQuestions.map(q => q.id);
    categorizeMutation.mutate(questionIds);
  };

  const handleCategorizeSelected = () => {
    if (selectedQuestions.length === 0) {
      toast({
        title: "No Questions Selected",
        description: "Please select questions to categorize",
        variant: "destructive"
      });
      return;
    }
    
    // Filter selected questions to only include uncategorized ones
    const selectedUncategorized = questions
      .filter(q => selectedQuestions.includes(q.id))
      .filter(q => 
        !q.category || 
        q.category === 'None' || 
        q.category === '' || 
        q.category === null ||
        q.category === undefined
      )
      .map(q => q.id);

    if (selectedUncategorized.length === 0) {
      toast({
        title: "No Uncategorized Questions Selected",
        description: "All selected questions already have categories assigned",
        variant: "default"
      });
      return;
    }

    categorizeMutation.mutate(selectedUncategorized);
  };

  const handleCategoryOverrideChange = (questionId: number, category: string) => {
    setResultOverrides((prev) => {
      const current = prev[questionId] || { category: "", keywords: [] as string[] };
      if (current.category === category) {
        return prev;
      }

      return {
        ...prev,
        [questionId]: {
          category,
          subCategory: "",
          keywords: [],
        },
      };
    });
  };

  const handleSubCategoryOverrideChange = (questionId: number, subCategory: string) => {
    setResultOverrides((prev) => {
      const current = prev[questionId] || { category: "", keywords: [] as string[] };
      return {
        ...prev,
        [questionId]: {
          ...current,
          subCategory,
        },
      };
    });
  };

  const handleToggleKeywordOverride = (questionId: number, keyword: string) => {
    setResultOverrides((prev) => {
      const current = prev[questionId] || { category: "", keywords: [] as string[] };
      const keywords = current.keywords.includes(keyword)
        ? current.keywords.filter((item) => item !== keyword)
        : [...current.keywords, keyword];

      return {
        ...prev,
        [questionId]: {
          ...current,
          keywords: normalizeKeywords(keywords),
        },
      };
    });
  };

  const handleRemoveKeywordOverride = (questionId: number, keyword: string) => {
    setResultOverrides((prev) => {
      const current = prev[questionId] || { category: "", keywords: [] as string[] };
      return {
        ...prev,
        [questionId]: {
          ...current,
          keywords: current.keywords.filter((item) => item !== keyword),
        },
      };
    });
  };

  const handleKeywordInputChange = (questionId: number, value: string) => {
    setKeywordInputs((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleAddKeywordOverride = (questionId: number) => {
    const value = keywordInputs[questionId]?.trim();
    if (!value) return;

    setResultOverrides((prev) => {
      const current = prev[questionId] || { category: "", keywords: [] as string[] };
      return {
        ...prev,
        [questionId]: {
          ...current,
          keywords: normalizeKeywords([...current.keywords, value]),
        },
      };
    });

    setKeywordInputs((prev) => ({
      ...prev,
      [questionId]: "",
    }));
  };

  const handleApplySelected = () => {
    if (selectedResults.length === 0) {
      toast({
        title: "No Categories Selected",
        description: "Please select categories to apply",
        variant: "destructive"
      });
      return;
    }

    const updates: Array<{
      questionId: number;
      category: string;
      subCategory?: string;
      keywords: string[];
      brand?: string;
    }> = [];
    const invalid: number[] = [];

    selectedResults.forEach((questionId) => {
      const result = categorizationResults.find(
        (item) => item.questionId === questionId && !item.error,
      );
      const override = resultOverrides[questionId];

      if (!result || !override || !override.category) {
        invalid.push(questionId);
        return;
      }

      updates.push({
        questionId,
        category: override.category,
        subCategory: override.subCategory || undefined,
        keywords: override.keywords || [],
        brand: result.suggestedBrand,
      });
    });

    if (invalid.length > 0 || updates.length === 0) {
      toast({
        title: "Missing Category Selection",
        description: "Please ensure each selected question has a category before applying.",
        variant: "destructive",
      });
      return;
    }

    applyMutation.mutate(updates);
  };

  const toggleQuestionSelection = (questionId: number) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const toggleResultSelection = (questionId: number) => {
    setSelectedResults(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const toggleAllQuestions = () => {
    const filteredQuestions = getFilteredQuestions();
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    }
  };

  const toggleAllResults = () => {
    const validResults = categorizationResults
      .filter(r => r.suggestedCategory && !r.error)
      .map(r => r.questionId);

    if (selectedResults.length === validResults.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(validResults);
    }
  };

  const toggleGroupCollapse = (groupKey: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredQuestions = getFilteredQuestions();
  const competitionOptions = getCompetitionOptions();

  if (loadingQuestions) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (questionsError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600 mb-2">Failed to load questions</p>
          <p className="text-xs text-gray-500">{questionsError.message}</p>
          <Button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/all-questions'] })}
            variant="outline"
            size="sm"
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI Question Categorization
          </CardTitle>
          <CardDescription>
            Automatically categorize competition questions using AI to organize your question library
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary Stats */}
          {summary && (
            <div className="flex gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              <span><strong>{summary.totalQuestions}</strong> questions</span>
              <span><strong>{summary.totalCompetitions}</strong> competitions</span>
              <span><strong>{summary.activeCompetitions}</strong> active competitions</span>
              <span><strong>{filteredQuestions.length}</strong> showing</span>
              <span className="text-orange-600"><strong>{getUncategorizedQuestions().length}</strong> need categorization</span>
            </div>
          )}

          {/* Filters and View Controls */}
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={viewMode} onValueChange={(value: 'grouped' | 'list') => setViewMode(value)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grouped">
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Grouped by Competition
                  </div>
                </SelectItem>
                <SelectItem value="list">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    List View
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCompetition} onValueChange={setFilterCompetition}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Competitions</SelectItem>
                <SelectItem value="no-competition">No Competition</SelectItem>
                {competitionOptions.map((comp) => (
                  <SelectItem key={comp.id} value={comp.id}>
                    <div className="flex items-center gap-2">
                      <span>{comp.title}</span>
                      <Badge variant={comp.isActive ? "default" : "secondary"} className="text-xs">
                        {comp.questionCount}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={(value: 'all' | 'active' | 'inactive') => setFilterStatus(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleCategorizeAll}
              disabled={categorizeMutation.isPending || getUncategorizedQuestions().length === 0}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="button-categorize-all"
            >
              {categorizeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Categorizing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Categorize Uncategorized ({getUncategorizedQuestions().length})
                </>
              )}
            </Button>
            <Button
              onClick={handleCategorizeSelected}
              disabled={categorizeMutation.isPending || selectedQuestions.length === 0}
              variant="outline"
              data-testid="button-categorize-selected"
            >
              Categorize Selected ({selectedQuestions.filter(id => {
                const q = questions.find(question => question.id === id);
                return q && (!q.category || q.category === 'None' || q.category === '' || q.category === null || q.category === undefined);
              }).length})
            </Button>
          </div>

          {/* Questions Display */}
          {questions && questions.length > 0 && categorizationResults.length === 0 && (
            <div className="border rounded-lg">
              {viewMode === 'grouped' ? (
                // Grouped View
                <div className="space-y-2">
                  {Object.entries(groupedQuestions)
                    .filter(([_, group]) => {
                      // Apply filters to groups
                      if (filterCompetition !== 'all') {
                        const groupKey = filterCompetition === 'no-competition' ? 'no-competition' : filterCompetition;
                        if (_ !== groupKey) return false;
                      }
                      if (filterStatus !== 'all') {
                        const matchesStatus = filterStatus === 'active' ? group.competitionIsActive : !group.competitionIsActive;
                        if (!matchesStatus) return false;
                      }
                      return true;
                    })
                    .map(([groupKey, group]) => {
                      const isCollapsed = collapsedGroups.has(groupKey);
                      return (
                        <Collapsible key={groupKey} open={!isCollapsed} onOpenChange={() => toggleGroupCollapse(groupKey)}>
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer border-b">
                              <div className="flex items-center gap-3">
                                {isCollapsed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                <div>
                                  <h3 className="font-medium">{group.competitionTitle}</h3>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                      {extractBrand(group.competitionTitle)}
                                    </Badge>
                                    {group.competitionType && (
                                      <Badge variant="outline" className="text-xs">
                                        {group.competitionType}
                                      </Badge>
                                    )}
                                    {group.competitionCategory && (
                                      <Badge variant="outline" className="text-xs">
                                        {group.competitionCategory}
                                      </Badge>
                                    )}
                                    <Badge variant={group.competitionIsActive ? "default" : "secondary"} className="text-xs">
                                      {group.competitionIsActive ? "Active" : "Inactive"}
                                    </Badge>
                                    <span>•</span>
                                    <span>{group.questions.length} questions</span>
                                    {group.competitionStartDate && (
                                      <>
                                        <span>•</span>
                                        <Calendar className="h-3 w-3" />
                                        <span>{formatDate(group.competitionStartDate)}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm">
                                {isCollapsed ? "Show" : "Hide"} Questions
                              </Button>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-12">
                                    <Checkbox
                                      checked={group.questions.every(q => selectedQuestions.includes(q.id))}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedQuestions(prev => [...new Set([...prev, ...group.questions.map(q => q.id)])]);
                                        } else {
                                          setSelectedQuestions(prev => prev.filter(id => !group.questions.map(q => q.id).includes(id)));
                                        }
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead>Question</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Current Category</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.questions.map((question) => (
                                  <TableRow key={question.id}>
                                    <TableCell>
                                      <Checkbox
                                        checked={selectedQuestions.includes(question.id)}
                                        onCheckedChange={() => toggleQuestionSelection(question.id)}
                                        data-testid={`checkbox-question-${question.id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium max-w-md">
                                      <div className="truncate" title={question.questionText}>
                                        {question.questionText}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-xs">
                                        {question.questionType}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {question.category && question.category !== 'None' && question.category !== '' ? (
                                        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                                          {question.category} ✓
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-orange-600 border-orange-200">
                                          None
                                        </Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                </div>
              ) : (
                // List View
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedQuestions.length === filteredQuestions.length}
                          onCheckedChange={toggleAllQuestions}
                          data-testid="checkbox-select-all-questions"
                        />
                      </TableHead>
                      <TableHead>Question</TableHead>
                      <TableHead>Competition</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Current Category</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuestions.map((question) => (
                      <TableRow key={question.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedQuestions.includes(question.id)}
                            onCheckedChange={() => toggleQuestionSelection(question.id)}
                            data-testid={`checkbox-question-${question.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium max-w-md">
                          <div className="truncate" title={question.questionText}>
                            {question.questionText}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-sm">{question.competitionTitle || 'No Competition'}</div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                {extractBrand(question.competitionTitle)}
                              </Badge>
                              {question.competitionType && (
                                <Badge variant="outline" className="text-xs">{question.competitionType}</Badge>
                              )}
                              <Badge variant={question.competitionIsActive ? "default" : "secondary"} className="text-xs">
                                {question.competitionIsActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {question.questionType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {question.category && question.category !== 'None' && question.category !== '' ? (
                            <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                              {question.category} ✓
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-200">
                              None
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {categorizationResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Apply Categories</CardTitle>
            <CardDescription>
              Review AI-suggested categories and apply them to your questions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={handleApplySelected}
                disabled={applyMutation.isPending || selectedResults.length === 0}
                data-testid="button-apply-categories"
              >
                {applyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Apply Selected ({selectedResults.length})
                  </>
                )}
              </Button>
              <Button
                onClick={() => setCategorizationResults([])}
                variant="outline"
                data-testid="button-cancel-categorization"
              >
                Cancel
              </Button>
            </div>

            <div className="border rounded-lg max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedResults.length > 0 && selectedResults.length === categorizationResults.filter(r => r.suggestedCategory && !r.error).length}
                        onCheckedChange={toggleAllResults}
                        data-testid="checkbox-select-all-results"
                      />
                    </TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Sub-Category</TableHead>
                    <TableHead>Keywords</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categorizationResults.map((result) => {
                    const question = questions.find(q => q.id === result.questionId);
                    const override = resultOverrides[result.questionId] || {
                      category: "",
                      subCategory: "",
                      keywords: [] as string[],
                    };
                    const categoryDefinition = CATEGORY_DEFINITIONS.find(
                      (definition) => definition.name === override.category,
                    );
                    const subCategoryOptions = categoryDefinition?.subCategories ?? [];
                    const keywordSuggestions = categoryDefinition?.keywords ?? [];
                    const keywordInputValue = keywordInputs[result.questionId] || "";
                    return (
                      <TableRow key={result.questionId}>
                        <TableCell>
                          {result.error ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Checkbox
                              checked={selectedResults.includes(result.questionId)}
                              onCheckedChange={() => toggleResultSelection(result.questionId)}
                              data-testid={`checkbox-result-${result.questionId}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-medium max-w-xs">
                          <div className="truncate" title={result.questionText}>
                            {result.questionText}
                          </div>
                          {question?.competitionTitle && (
                            <div className="text-xs text-gray-500 mt-1">{question.competitionTitle}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.error ? (
                            <Badge variant="destructive">Error</Badge>
                          ) : (
                            <div className="space-y-2">
                              {result.currentCategory && (
                                <div className="text-xs text-gray-500">
                                  Current:{" "}
                                  <Badge variant="outline" className="text-xs">
                                    {result.currentCategory}
                                  </Badge>
                                </div>
                              )}
                              <Select
                                value={override.category || ""}
                                onValueChange={(value) =>
                                  handleCategoryOverrideChange(result.questionId, value)
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CATEGORY_DEFINITIONS.map((category) => (
                                    <SelectItem key={category.name} value={category.name}>
                                      {category.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!override.category && (
                                <p className="text-xs text-muted-foreground">
                                  Choose a category to continue.
                                </p>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.error ? (
                            <span className="text-xs text-gray-400">N/A</span>
                          ) : (
                            <div className="space-y-2">
                              {result.currentSubCategory && (
                                <div className="text-xs text-gray-500">
                                  Current:{" "}
                                  <Badge variant="outline" className="text-xs">
                                    {result.currentSubCategory}
                                  </Badge>
                                </div>
                              )}
                              <Select
                                value={override.subCategory || ""}
                                onValueChange={(value) =>
                                  handleSubCategoryOverrideChange(result.questionId, value)
                                }
                                disabled={!override.category || subCategoryOptions.length === 0}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select sub-category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {subCategoryOptions.map((subCategory) => (
                                    <SelectItem key={subCategory} value={subCategory}>
                                      {subCategory}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!override.category && (
                                <p className="text-xs text-muted-foreground">
                                  Select a category first.
                                </p>
                              )}
                              {override.category && subCategoryOptions.length === 0 && (
                                <p className="text-xs text-muted-foreground">
                                  No sub-categories available for this category.
                                </p>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.error ? (
                            <span className="text-xs text-gray-400">N/A</span>
                          ) : (
                            <div className="space-y-2 max-w-xs">
                              {override.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {override.keywords.map((keyword) => (
                                    <Badge
                                      key={keyword}
                                      variant="secondary"
                                      className="flex items-center gap-1 text-xs"
                                    >
                                      {keyword}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRemoveKeywordOverride(result.questionId, keyword)
                                        }
                                        className="rounded-full p-0.5 hover:text-destructive focus:outline-none focus:ring-1 focus:ring-ring"
                                        aria-label={`Remove keyword ${keyword}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              {override.category ? (
                                keywordSuggestions.length > 0 ? (
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">
                                      Suggested keywords
                                    </p>
                                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                      {keywordSuggestions.map((keyword) => {
                                        const isSelected = override.keywords.includes(keyword);
                                        return (
                                          <Badge
                                            key={keyword}
                                            variant={isSelected ? "default" : "outline"}
                                            className="cursor-pointer hover:bg-muted text-xs"
                                            onClick={() =>
                                              handleToggleKeywordOverride(result.questionId, keyword)
                                            }
                                          >
                                            {keyword}
                                            {isSelected ? " ✓" : " +"}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    No predefined keywords for this category.
                                  </p>
                                )
                              ) : (
                                <p className="text-xs text-muted-foreground">
                                  Select a category to view suggested keywords.
                                </p>
                              )}

                              <div className="flex gap-2">
                                <Input
                                  value={keywordInputValue}
                                  onChange={(event) =>
                                    handleKeywordInputChange(result.questionId, event.target.value)
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      handleAddKeywordOverride(result.questionId);
                                    }
                                  }}
                                  placeholder="Add custom keyword"
                                  disabled={!override.category}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAddKeywordOverride(result.questionId)}
                                  disabled={
                                    !override.category || keywordInputValue.trim().length === 0
                                  }
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.suggestedBrand ? (
                            <Badge variant="default" className="text-xs bg-blue-600">
                              {result.suggestedBrand}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.confidence && (
                            <Badge variant="secondary">
                              {Math.round(result.confidence * 100)}%
                            </Badge>
                          )}
                          {result.error && (
                            <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={result.error}>
                              {result.error}
                            </div>
                          )}
                          {result.reasoning && !result.error && (
                            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={result.reasoning}>
                              {result.reasoning}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {questions && questions.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No competition questions found in the database</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Placeholder for the new Answers Tab component
function QuestionAnswersTab() {
  const { toast } = useToast();
  const [selectedCompetitions, setSelectedCompetitions] = useState<string[]>([]); // Start with no competitions selected
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Fixed page size
  const [fetchTrigger, setFetchTrigger] = useState(0); // Add trigger to control when to fetch

  // Fetch questions and competitions for filters first
  const { data: questionsResponse, isLoading: loadingQuestions } = useQuery<QuestionsResponse>({
    queryKey: ['/api/admin/all-questions'],
    queryFn: async () => {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Authentication required');
      const response = await fetch('/api/admin/all-questions', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch questions');
      return response.json();
    },
    enabled: !!supabase
  });

  const allQuestions = questionsResponse?.questions || [];
  const allCompetitions = questionsResponse?.groupedByCompetition || {};

  // Build query parameters for filtered answers
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('limit', pageSize.toString());
    
    if (selectedCompetitions.includes('all')) {
      params.set('competitions', 'all');
    } else if (selectedCompetitions.length > 0) {
      // Filter out 'all' and only include valid competition IDs
      const validCompetitions = selectedCompetitions.filter(id => id !== 'all');
      if (validCompetitions.length > 0) {
        params.set('competitions', validCompetitions.join(','));
      }
    }
    return params.toString();
  };

  // Fetch answers with pagination
  const { data: answersResponse, isLoading: loadingAnswers, error: answersError } = useQuery<AnswersResponse>({
    queryKey: ['/api/admin/answers', selectedCompetitions, currentPage, pageSize, fetchTrigger],
    queryFn: async () => {
      console.log('[QUESTION ANSWERS] Fetching answers...');
      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Authentication required');
        }

        let endpoint: string;
        let queryParams: string;

        if (selectedCompetitions.includes('all')) {
          // Use the all-answers endpoint for fetching all answers
          endpoint = '/api/admin/all-answers';
          const params = new URLSearchParams();
          params.set('page', currentPage.toString());
          params.set('limit', pageSize.toString());
          queryParams = params.toString();
          console.log('[QUESTION ANSWERS] Using all-answers endpoint');
        } else {
          // Use filtered-answers endpoint for specific competitions
          endpoint = '/api/admin/filtered-answers';
          queryParams = buildQueryParams();
          console.log('[QUESTION ANSWERS] Using filtered-answers endpoint for competitions:', selectedCompetitions);
        }

        const response = await fetch(`${endpoint}?${queryParams}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch answers: ${response.status} ${errorText}`);
        }
        const data = await response.json();
        console.log('[QUESTION ANSWERS] Answers received:', data.pagination || data.answers?.length);
        return data;
      } catch (error) {
        console.error('[QUESTION ANSWERS] Fetch error:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
    enabled: !!supabase && selectedCompetitions.length > 0 && fetchTrigger > 0 // Only fetch when explicitly triggered
  });

  const answers = answersResponse?.answers || [];
  const pagination = answersResponse?.pagination;

  // Competition options for multi-select filter
  const getCompetitionFilterOptions = () => {
    const competitions = Object.values(allCompetitions).map(group => ({
      id: group.competitionId?.toString() || 'no-competition',
      title: group.competitionTitle,
      questionCount: group.questions.length
    }));
    return competitions.sort((a, b) => a.title.localeCompare(b.title));
  };

  const competitionFilterOptions = getCompetitionFilterOptions();

  // Handle competition selection
  const handleCompetitionToggle = (competitionId: string) => {
    if (competitionId === 'all') {
      if (selectedCompetitions.includes('all')) {
        setSelectedCompetitions(competitionFilterOptions.map(comp => comp.id)); // Deselect all
      } else {
        setSelectedCompetitions(['all']); // Select all
      }
    } else {
      if (selectedCompetitions.includes('all')) {
        setSelectedCompetitions(competitionFilterOptions.map(comp => comp.id).filter(id => id !== competitionId)); // Remove 'all' and add selected
      } else {
        setSelectedCompetitions(prev =>
          prev.includes(competitionId)
            ? prev.filter(id => id !== competitionId)
            : [...prev, competitionId]
        );
      }
    }
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle fetch all answers button click
  const handleFetchAllAnswers = () => {
    setSelectedCompetitions(['all']); // Select 'all' to indicate fetching all answers
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1); // Trigger the fetch
  };

  // Handle select all/none competitions
  const handleSelectAllCompetitions = () => {
    if (selectedCompetitions.length === competitionFilterOptions.length) {
      setSelectedCompetitions([]);
    } else {
      setSelectedCompetitions(competitionFilterOptions.map(comp => comp.id));
    }
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFetchTrigger(prev => prev + 1); // Trigger fetch for new page
  };

  const totalPages = Math.ceil((pagination?.total || 0) / pageSize);

  if (loadingQuestions) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">Loading competitions...</p>
      </div>
    );
  }

  if (loadingAnswers) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">Loading answers...</p>
      </div>
    );
  }

  if (answersError) {
    return (
      <div className="flex items-center justify-center p-8">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-600 mb-2">Failed to load answers</p>
        <p className="text-xs text-gray-500">{answersError.message}</p>
        <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/filtered-answers'] })} variant="outline" size="sm" className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-green-500" />
          Question Answers
        </CardTitle>
        <CardDescription>
          View and analyze submitted answers for questions from selected competitions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {(pagination || answers.length > 0) && (
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg items-center justify-between">
            <div className="flex gap-4">
              {pagination && (
                <>
                  <span><strong>{pagination.total}</strong> total answers</span>
                  <span><strong>{answers.length}</strong> showing</span>
                  <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                </>
              )}
              {selectedCompetitions.includes('all') ? (
                <span>All competitions selected</span>
              ) : selectedCompetitions.length > 0 ? (
                <span><strong>{selectedCompetitions.length}</strong> competitions selected</span>
              ) : (
                <span>No competitions selected</span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Button
                variant={selectedCompetitions.includes('all') ? "default" : "outline"}
                size="sm"
                onClick={handleFetchAllAnswers}
                className={selectedCompetitions.includes('all') ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              >
                📊 Fetch All Answers
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllCompetitions}
                disabled={selectedCompetitions.includes('all')}
              >
                {selectedCompetitions.length === competitionFilterOptions.length ? 'Deselect All' : 'Select All'}
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  // Trigger fetch for selected competitions
                  if (selectedCompetitions.length > 0 && !selectedCompetitions.includes('all')) {
                    setCurrentPage(1);
                    setFetchTrigger(prev => prev + 1); // Trigger the fetch
                  }
                }}
                disabled={selectedCompetitions.length === 0 || selectedCompetitions.includes('all')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                📋 Fetch Selected ({selectedCompetitions.filter(id => id !== 'all').length})
              </Button>
              
              <span className="text-sm text-gray-600">
                {selectedCompetitions.includes('all') 
                  ? 'All competitions selected' 
                  : `${selectedCompetitions.length} of ${competitionFilterOptions.length} competitions selected`
                }
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
            {competitionFilterOptions.map((comp) => (
              <div key={comp.id} className="flex items-center space-x-2">
                <Checkbox
                  id={comp.id}
                  checked={selectedCompetitions.includes(comp.id)}
                  onCheckedChange={() => handleCompetitionToggle(comp.id)}
                  disabled={selectedCompetitions.includes('all')}
                />
                <label htmlFor={comp.id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                  {comp.title}
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {comp.questionCount} questions
                  </Badge>
                </label>
              </div>
            ))}
          </div>
        </div>

        {answers.length > 0 ? (
          <>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Question</TableHead>
                    <TableHead>Competition</TableHead>
                    <TableHead>Answer</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Submitted At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {answers.map((answer) => (
                    <TableRow key={answer.id}>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={answer.questionText || 'N/A'}>
                          {answer.questionText || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={answer.competitionTitle || 'N/A'}>
                          {answer.competitionTitle || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-lg">
                        <div className="truncate" title={answer.answerText}>
                          {answer.answerText}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={answer.username || answer.userEmail || 'N/A'}>
                          {answer.username || answer.userEmail || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(answer.submittedAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Go to page:</span>
                  <Select value={currentPage.toString()} onValueChange={(value) => handlePageChange(parseInt(value))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <SelectItem key={page} value={page.toString()}>
                          {page}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>
              {selectedCompetitions.includes('all')
                ? "No answers found in the database."
                : selectedCompetitions.length === 0
                  ? "Please select one or more competitions to view answers."
                  : "No answers found for the selected competitions."
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function AdminPage() {
  return (
    <Tabs defaultValue="categorization" className="w-full p-4">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="categorization" className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          Question Categorization
        </TabsTrigger>
        <TabsTrigger value="answers" className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Question Answers
        </TabsTrigger>
      </TabsList>

      <TabsContent value="categorization">
        <QuestionCategorizationContent />
      </TabsContent>

      <TabsContent value="answers">
        <QuestionAnswersTab />
      </TabsContent>
    </Tabs>
  );
}
